/**
 * The door's form as the window of the theme shows it (D37): the props of `DoorWindow` for
 * each step of the configurator's rail, from the unsaved draft.
 *
 * The participant page gets the texts parsed by the server; the draft holds them as typed.
 * The preview shows them as plain paragraphs (`plainBlocks`), and in the configurator the
 * texts are edited in place anyway. Stored files have no address the backoffice may load
 * before their release, so a stand-in shows where they stand, with their text.
 */
/** The paragraphs of a typed text, as the window renders them; empty lines separate them. */
export function plainBlocks(text) {
    return text
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph !== "")
        .map((paragraph) => ({
        kind: "paragraph",
        lines: paragraph.split("\n").map((line) => [{ text: line }]),
    }));
}
/**
 * A neutral picture for a stored file the preview cannot load: a grey surface with a frame
 * and a mountain, drawn here, no file and no request. Wide, so `auto` shows it as 16:9.
 */
export const STAND_IN_PICTURE = "data:image/svg+xml," +
    encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 90">' +
        '<rect width="160" height="90" fill="#e2e8f0"/>' +
        '<path d="M48 66 70 40l14 16 10-10 18 20z" fill="#94a3b8"/>' +
        '<circle cx="102" cy="32" r="7" fill="#94a3b8"/></svg>');
/**
 * The place of a picture the door has not got yet: a dashed frame on white. In the editor only,
 * so an empty element can still be chosen and moved (22.10.2026).
 */
export const EMPTY_PICTURE = {
    src: "data:image/svg+xml," +
        encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 90">' +
            '<rect x="1" y="1" width="158" height="88" fill="#fff" stroke="#94a3b8" stroke-width="1.5" ' +
            'stroke-dasharray="6 4" vector-effect="non-scaling-stroke"/>' +
            '<path d="M60 62 74 45l9 10 7-7 12 14z" fill="#cbd5e1"/>' +
            '<circle cx="95" cy="38" r="5" fill="#cbd5e1"/></svg>'),
    alt: "Noch kein Bild",
};
/** The picture a stored file stands for in the preview. */
export function standIn(alt, video = false) {
    const text = alt?.trim() || (video ? "Video" : "Bild");
    return { src: STAND_IN_PICTURE, alt: video ? `Video: ${text}` : text };
}
/** The result's heading and sentence, as the participant page says them after a wrong answer. */
export function wrongAnswerWords(questionCount) {
    // With several questions the page counts the right ones; one wrong stands for every case.
    return questionCount > 1
        ? {
            title: `${String(questionCount - 1)} von ${String(questionCount)} Antworten sind richtig.`,
            sentence: "Teilnehmen können Sie, wenn alle Antworten richtig sind.",
        }
        : {
            title: "Ihre Antwort ist leider nicht richtig.",
            sentence: "Teilnehmen können Sie mit der richtigen Antwort.",
        };
}
/**
 * A door's own words after a wrong answer with the counts put in (24.10.2026): `{richtig}` and
 * `{fragen}`, as the participant page does it (`withCounts` there). The preview counts one wrong.
 */
export function withCounts(text, questionCount) {
    return text
        .replaceAll("{richtig}", String(Math.max(0, questionCount - 1)))
        .replaceAll("{fragen}", String(questionCount));
}
//# sourceMappingURL=preview.js.map